import {useState, useCallback, useEffect, useRef} from "react";
import {ReactComponent as DropdownOpened} from "assets/image/Layout/ObjectPanel/Layer/ic_dropdown.svg";
import {ReactComponent as DropdownClosed} from "assets/image/Layout/ObjectPanel/Layer/ic_dropdown_right.svg";
import {ReactComponent as Visible} from "assets/image/Layout/ObjectPanel/Layer/ic_visible.svg";
import {ReactComponent as Invisible} from "assets/image/Layout/ObjectPanel/Layer/ic_invisible.svg";
import {ObjectIcon} from "./icons";
import styles from "./index.module.scss";

export default function TreeNode({
  nodeObject,
  depth,
  isRootNode,
  selectedKeys,
  selectNodeObject,
  toggleVisibility,
  isDragging,
  onDragEnter,
  onDragOver,
  onDrop,
}) {
  const {name, type, title, uuid, userData, children} = nodeObject;

  const isSelected = uuid === selectedKeys[0];
  const onClickNode = useCallback(() => {
    selectNodeObject(uuid);
  }, [uuid]);

  const isConcealed = nodeObject.layers.mask === 16;
  const onClickVisible = () => {
    toggleVisibility(uuid, isConcealed);
  };

  const [isExpanded, setIsExpanded] = useState(getIsExpanded(isRootNode, nodeObject));
  const onClickDropdown = useCallback(() => {
    setIsExpanded(!isExpanded);
    nodeObject.isExpanded = !isExpanded;
  }, [isExpanded]);

  useEffect(() => {
    if (children.length > 0) return;
    setIsExpanded(false);
    nodeObject.isExpanded = false;
  }, [children.length]);

  const node = useRef();
  const siblingGap = useRef();
  const childrenGap = useRef();

  useEffect(() => {
    const targetElements = [node.current, siblingGap.current, childrenGap.current];
    if (isDragging) {
      targetElements.forEach(ele => {
        if (!ele) return;
        ele.addEventListener("dragenter", onDragEnter);
        ele.addEventListener("dragover", onDragOver);
        ele.addEventListener("drop", onDrop);
      });
    }
    return () => {
      targetElements.forEach(ele => {
        if (!ele) return;
        ele.removeEventListener("dragenter", onDragEnter);
        ele.removeEventListener("dragover", onDragOver);
        ele.removeEventListener("drop", onDrop);
      });
    };
  }, [isDragging]);

  const titleWidth = 133 - 20 * depth;
  const siblingGapWidth = 208 - 20 * depth;
  const childrenGapWidth = 208 - 20 * (depth + 1);

  return (
    <div className={styles.container} uuid={uuid}>
      <div className={`${styles.content} ${isSelected ? styles.selected_node : ""}`} draggable={!isRootNode} ref={node} location={"node"}>
        <div className={styles.icon_front}>
          {children.length > 0 && <DropdownIcon isExpanded={isExpanded} onClick={onClickDropdown} />}
          <ObjectIcon type={type} name={name} userData={userData} />
        </div>
        <div className={styles.title} name={title} style={{width: titleWidth}} onClick={onClickNode}>
          {title}
        </div>
        <div className={styles.icon_back}>
          <VisibleIcon isConcealed={isConcealed} onClick={onClickVisible} />
        </div>
      </div>
      {isExpanded && (
        <>
          <div className={styles.gap}>
            <div
              className={styles.gap_indicator}
              style={{width: childrenGapWidth, marginLeft: 240 - childrenGapWidth, zIndex: depth}}
              ref={childrenGap}
              location={"childrenGap"}
            >
              <div className={styles.gap_area}></div>
            </div>
          </div>
          <div className={styles.children}>
            {children.map(child => (
              <TreeNode
                key={child.uuid}
                nodeObject={child}
                depth={depth + 1}
                isRootNode={false}
                selectedKeys={selectedKeys}
                selectNodeObject={selectNodeObject}
                toggleVisibility={toggleVisibility}
                isDragging={isDragging}
                onDragEnter={onDragEnter}
                onDragOver={onDragOver}
                onDrop={onDrop}
              />
            ))}
          </div>
        </>
      )}
      {!isRootNode && (
        <div className={styles.gap}>
          <div
            className={styles.gap_indicator}
            style={{width: siblingGapWidth, marginLeft: 240 - siblingGapWidth, zIndex: depth}}
            ref={siblingGap}
            location={"siblingGap"}
          >
            <div className={styles.gap_area}></div>
          </div>
        </div>
      )}
    </div>
  );
}

function getIsExpanded(isRootNode, nodeObject) {
  let isExpanded;
  if (nodeObject.children.length > 0) {
    if (isRootNode && nodeObject.isExpanded === undefined) {
      isExpanded = true;
    } else {
      isExpanded = !!nodeObject.isExpanded;
    }
  } else {
    isExpanded = false;
  }
  nodeObject.isExpanded = isExpanded;
  return isExpanded;
}

function DropdownIcon({isExpanded, onClick}) {
  if (isExpanded) return <DropdownOpened className={styles.dropdown} onClick={onClick} />;
  else return <DropdownClosed className={styles.dropdown} onClick={onClick} />;
}

function VisibleIcon({isConcealed, onClick}) {
  if (isConcealed) return <Invisible onClick={onClick} />;
  else return <Visible className={styles.transparent} onClick={onClick} />;
}
